import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import ConfirmModal from './ConfirmModal';
import QuizFlashcards from './QuizFlashcards';
import NotesAssignmentView from '../notes/NotesAssignmentView';
import 'katex/dist/katex.min.css';
import './AIChatAssistant.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Preset prompts shown as one-tap chips after the user attaches an image or PDF.
const IMAGE_QUICK_ACTIONS = [
  { id: 'solve', icon: '🧮', label: 'Solve this', prompt: 'Solve the problem in the attached file. Show your reasoning step by step.' },
  { id: 'check', icon: '✅', label: 'Check answer', prompt: 'Check the answer in the attached file. Tell me clearly whether each step and the final answer are correct, and explain any mistakes.' },
  { id: 'explain', icon: '💡', label: 'Explain', prompt: 'Explain the concept and approach behind the problem in the attached file, step by step, like a tutor would.' },
  { id: 'hint', icon: '🧭', label: 'Give me a hint', prompt: "Give me a single helpful hint to make progress on the problem in the attached file — don't reveal the full solution." },
];

// Suggested starter prompts shown on the empty-state welcome screen so a new
// chat has an obvious first move instead of a blank textbox.
const STARTER_PROMPTS = [
  { icon: '🧮', label: 'Help with a problem' },
  { icon: '🎯', label: 'Explain a concept simply' },
  { icon: '✅', label: 'Check work' },
  { icon: '💡', label: 'Give a study tip' },
];

const normalizeTimestamp = (timestamp) => {
  if (!timestamp) return null;

  // Backend may send UTC without timezone, e.g. "2026-07-09T12:04:20.123456"
  // Browser treats that as local time unless we add "Z".
  let value = timestamp;
  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(value)
  ) {
    value = `${value}Z`;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatMessageTime = (timestamp) => {
  const date = normalizeTimestamp(timestamp);
  if (!date) return '';

  return new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
    .format(date)
    .replace(/\s?(am|pm)$/i, (_, meridiem) => ` ${meridiem.toUpperCase()}`);
};

const formatRecordingTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const pickSupportedAudioMimeType = () => {
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return undefined;
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return candidates.find((type) => MediaRecorder.isTypeSupported?.(type));
};

// Fetches an authenticated chat attachment as a blob URL so it can be
// rendered (or linked to) even though the endpoint requires a Bearer token.
// Images render inline; PDFs render as a file chip that opens the blob in a
// new tab, since a PDF can't be shown with a plain <img>.
const AuthenticatedAttachmentPreview = ({ attachment, fallbackUrl }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [errored, setErrored] = useState(false);
  const isPdf = attachment?.kind === 'pdf' || attachment?.content_type === 'application/pdf';

  useEffect(() => {
    if (fallbackUrl) {
      setBlobUrl(fallbackUrl);
      return;
    }
    let revoked = false;
    let createdUrl = null;
    const load = async () => {
      try {
        const token = localStorage.getItem('edsupp_token');
        if (!token || !attachment?.url) return;
        const apiPath = attachment.url.startsWith('/api/')
          ? attachment.url.slice(4)
          : attachment.url;
        const response = await axios.get(`${BACKEND_URL}/api${apiPath}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        if (revoked) return;
        createdUrl = URL.createObjectURL(response.data);
        setBlobUrl(createdUrl);
      } catch (err) {
        console.error('Failed to load chat attachment:', err);
        setErrored(true);
      }
    };
    load();
    return () => {
      revoked = true;
      if (createdUrl) {
        try { URL.revokeObjectURL(createdUrl); } catch (_) { /* ignore */ }
      }
    };
  }, [attachment?.url, fallbackUrl]);

  if (errored) {
    return <div className="ai-chat-attachment-error">Could not load {isPdf ? 'file' : 'image'}</div>;
  }
  if (!blobUrl) {
    return <div className="ai-chat-attachment-loading">Loading {isPdf ? 'file' : 'image'}…</div>;
  }
  if (isPdf) {
    return (
      <a
        href={blobUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="ai-chat-attachment-pdf"
      >
        <span aria-hidden="true">📄</span>
        <span className="ai-chat-attachment-pdf-name">{attachment?.name || 'document.pdf'}</span>
      </a>
    );
  }
  return (
    <img
      src={blobUrl}
      alt={attachment?.name || 'attachment'}
      className="ai-chat-attachment-image"
    />
  );
};

const AIChatAssistant = ({ user, childId = null, batchId = null, isChildView = false, taskId = null, evaluationId = null, noteId = null, isSuppressed = false, externalRequest = null, noBottomNav = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [selectedModel, setSelectedModel] = useState('gpt-5');
  const [error, setError] = useState(null);
  const [hasAccess, setHasAccess] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [areModelsLoaded, setAreModelsLoaded] = useState(false);
  const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [pendingExternalMessage, setPendingExternalMessage] = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [attachmentPreviews, setAttachmentPreviews] = useState({});
  const [isMathMode, setIsMathMode] = useState(false);
  const [isMathLiveLoaded, setIsMathLiveLoaded] = useState(false);
  const [mathKeyboardOffset, setMathKeyboardOffset] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  // Hint-first (Socratic) mode: Eddy guides with questions/hints instead of
  // handing over the full answer right away. Resets like other UI toggles
  // (model, math mode) when the widget is reopened — it isn't persisted.
  const [isHintMode, setIsHintMode] = useState(false);
  const [showPracticeForm, setShowPracticeForm] = useState(false);
  const [practiceTopic, setPracticeTopic] = useState('');
  const [practiceCount, setPracticeCount] = useState(5);
  // Holds the saved content_items doc (same shape as a notes-flow quiz) once
  // "Assign this quiz" has persisted it server-side, so NotesAssignmentView
  // (the same assign UI used for Notes) can be opened for it.
  const [quizToAssign, setQuizToAssign] = useState(null);
  const [isSavingQuiz, setIsSavingQuiz] = useState(false);
  // Per-message "listen" playback state. Only one message's audio can be
  // loaded into the shared <audio> element at a time; ttsActiveIdx tracks
  // which one, ttsIsPlaying/ttsCurrentTime/ttsDuration mirror the element's
  // own play state and progress for the play/pause/stop + progress bar UI.
  const [ttsActiveIdx, setTtsActiveIdx] = useState(null);
  const [ttsIsPlaying, setTtsIsPlaying] = useState(false);
  const [ttsCurrentTime, setTtsCurrentTime] = useState(0);
  const [ttsDuration, setTtsDuration] = useState(0);
  const [ttsLoadingIdx, setTtsLoadingIdx] = useState(null);
  const ttsAudioCacheRef = useRef({});
  const ttsAudioRef = useRef(null);
  // Matches the 768px CSS breakpoint used throughout AIChatAssistant.css.
  // The compose input is too narrow on phones to fit the full desktop
  // placeholder on one line, so a shorter string is used there instead of
  // letting it wrap and get clipped by the single-row textarea.
  const [isCompactViewport, setIsCompactViewport] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const lastExternalRequestIdRef = useRef(null);
  const fileInputRef = useRef(null);
  const mathFieldRef = useRef(null);
  const historyPanelRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const discardRecordingRef = useRef(false);

  // Check subscription and AI chat access on mount
  useEffect(() => {
    checkAIChatAccess();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsCompactViewport(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!externalRequest?.id || externalRequest.id === lastExternalRequestIdRef.current) {
      return;
    }

    lastExternalRequestIdRef.current = externalRequest.id;
    setPendingExternalMessage(externalRequest.message);
    setIsOpen(true);
  }, [externalRequest]);

  useEffect(() => {
    if (!pendingExternalMessage) {
      return;
    }

    if (hasAccess === false) {
      setError('AI chat is unavailable for your account.');
      setPendingExternalMessage(null);
      return;
    }

    if (!isOpen || isLoading || hasAccess !== true || !areModelsLoaded) {
      return;
    }

    sendMessageWithContent(pendingExternalMessage);
    setPendingExternalMessage(null);
  }, [pendingExternalMessage, isOpen, isLoading, hasAccess, areModelsLoaded, selectedProvider, selectedModel]);

  // Load available models and the most recent conversation on open
  useEffect(() => {
    if (isOpen) {
      loadAvailableModels();
      openMostRecentConversation();
    }
  }, [isOpen]);

  // Close the chat-history dropdown when clicking outside of it
  useEffect(() => {
    if (!showHistoryPanel) return;
    const handleClickOutside = (e) => {
      if (historyPanelRef.current && !historyPanelRef.current.contains(e.target)) {
        setShowHistoryPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHistoryPanel]);

  const checkAIChatAccess = async () => {
    try {
      const token = localStorage.getItem('edsupp_token');
      if (!token) {
        setHasAccess(false);
        return;
      }

      try {
        const subResponse = await axios.get(`${BACKEND_URL}/api/billing/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setSubscription(subResponse.data);
      } catch (subscriptionError) {
        console.warn('Error fetching subscription details for AI chat:', subscriptionError);
      }

      // Check entitlement for AI chat questions (resolves school teacher -> school admin)
      const entitlementResponse = await axios.get(
        `${BACKEND_URL}/api/billing/entitlements/ai_chat_questions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setHasAccess(entitlementResponse.data.allowed);
    } catch (error) {
      console.error('Error checking AI chat access:', error);
      // If billing API fails, allow access (backward compatibility)
      setHasAccess(true);
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // MathLive's on-screen keyboard is a viewport-fixed overlay (a direct
  // child of <body>) that only auto-pads <body> to stay clear of page
  // content. Since our chat widget is itself position:fixed, that
  // auto-padding never reaches it, so the keyboard renders on top of the
  // input instead of above it. Track its live height via 'geometrychange'
  // and shift the whole widget up by that amount to compensate.
  useEffect(() => {
    if (!isMathMode || !isMathLiveLoaded || typeof window === 'undefined' || !window.mathVirtualKeyboard) {
      setMathKeyboardOffset(0);
      return;
    }
    const keyboard = window.mathVirtualKeyboard;
    const handleGeometryChange = () => setMathKeyboardOffset(keyboard.boundingRect?.height || 0);
    keyboard.addEventListener('geometrychange', handleGeometryChange);
    handleGeometryChange();
    return () => {
      keyboard.removeEventListener('geometrychange', handleGeometryChange);
      setMathKeyboardOffset(0);
    };
  }, [isMathMode, isMathLiveLoaded]);

  // Lazy-load MathLive (registers the <math-field> custom element) only when
  // the math keyboard is first toggled on, to keep it out of the main bundle.
  useEffect(() => {
    if (!isMathMode || isMathLiveLoaded) return;
    import('mathlive').then(() => setIsMathLiveLoaded(true));
  }, [isMathMode, isMathLiveLoaded]);

  // <math-field> is an uncontrolled custom element; sync its value into
  // inputMessage via a native 'input' listener instead of a React value prop.
  useEffect(() => {
    const field = mathFieldRef.current;
    if (!isMathMode || !isMathLiveLoaded || !field) return;
    field.value = inputMessage;
    const handleMathInput = (e) => setInputMessage(e.target.value);
    field.addEventListener('input', handleMathInput);
    return () => field.removeEventListener('input', handleMathInput);
  }, [isMathMode, isMathLiveLoaded]);

  const toggleMathMode = () => {
    if (isLoading || hasAccess !== true) return;
    setIsMathMode(prev => !prev);
  };

  // Stop any in-flight recording/timer if the widget unmounts mid-recording.
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const transcribeRecording = async (blob) => {
    setIsTranscribing(true);
    try {
      const token = localStorage.getItem('edsupp_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }
      const ext = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('ogg') ? 'ogg' : 'webm';
      const formData = new FormData();
      formData.append('file', blob, `voice-message.${ext}`);
      const response = await axios.post(`${BACKEND_URL}/api/chat/transcribe`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const transcript = (response.data?.transcript || '').trim();
      if (transcript) {
        setInputMessage((prev) => (prev.trim() ? `${prev.trim()} ${transcript}` : transcript));
      } else {
        setError("Didn't catch that — please try recording again.");
      }
    } catch (err) {
      console.error('Voice transcription failed:', err);
      setError(err.response?.data?.detail || 'Could not transcribe your recording. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const startRecording = async () => {
    if (isRecording || isTranscribing || isLoading || hasAccess !== true) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      setError('Voice input is not supported in this browser.');
      return;
    }
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mimeType = pickSupportedAudioMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      discardRecordingRef.current = false;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        if (!discardRecordingRef.current && audioChunksRef.current.length > 0) {
          const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
          transcribeRecording(blob);
        }
        audioChunksRef.current = [];
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
      setIsRecording(true);
    } catch (err) {
      console.error('Microphone access failed:', err);
      setError('Could not access the microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;
    discardRecordingRef.current = false;
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
    mediaRecorderRef.current?.stop();
  };

  const cancelRecording = () => {
    if (!isRecording) return;
    discardRecordingRef.current = true;
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
    mediaRecorderRef.current?.stop();
  };


  // The field is uncontrolled, so clearing inputMessage after a send (or on
  // toggling math mode back on with a stale value) doesn't clear the
  // on-screen equation by itself — without this the old formula stays
  // visible even though it was already sent.
  useEffect(() => {
    const field = mathFieldRef.current;
    if (!isMathMode || !isMathLiveLoaded || !field) return;
    if (inputMessage === '' && field.value !== '') {
      field.value = '';
    }
  }, [inputMessage, isMathMode, isMathLiveLoaded]);

  const loadAvailableModels = async () => {
    setAreModelsLoaded(false);
    try {
      const token = localStorage.getItem('edsupp_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }
      const response = await axios.get(`${BACKEND_URL}/api/chat/models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const models = response.data.models || [];
      const defaultConfig = response.data.default || null;
      
      setAvailableModels(models);
      
      // Set default model from system configuration or first available
      if (defaultConfig && defaultConfig.provider && defaultConfig.model_id) {
        setSelectedProvider(defaultConfig.provider);
        setSelectedModel(defaultConfig.model_id);
      } else if (models.length > 0) {
        const defaultModel = models[0];
        setSelectedProvider(defaultModel.provider);
        setSelectedModel(defaultModel.model_id);
      }
    } catch (err) {
      console.error('Error loading models:', err);
      setError('Could not load AI models. Please check API key configuration.');
    } finally {
      setAreModelsLoaded(true);
    }
  };

  // Loads the messages belonging to one chat thread and makes it the active
  // conversation. Pass null to clear the view for a brand-new thread.
  const loadChatHistory = async (targetConversationId) => {
    setIsHistoryLoaded(false);
    setConversationId(targetConversationId || null);
    stopPlayback();
    ttsAudioCacheRef.current = {};
    if (!targetConversationId) {
      setMessages([]);
      setIsHistoryLoaded(true);
      return;
    }
    try {
      const token = localStorage.getItem('edsupp_token');
      if (!token) return;
      const response = await axios.get(`${BACKEND_URL}/api/chat/history`, {
        params: { limit: 200, conversation_id: targetConversationId, child_id: childId || undefined, is_child_view: isChildView },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const history = response.data || [];
      // Convert history to message format (reverse to show oldest first)
      const historyMessages = history.reverse().flatMap(item => [
        {
          role: 'user',
          content: item.user_message,
          timestamp: item.timestamp,
          attachments: item.attachments || []
        },
        {
          role: 'assistant',
          content: item.assistant_response,
          timestamp: item.timestamp,
          nextSteps: item.next_steps || null,
          quizQuestions: item.quiz || null,
        }
      ]);
      
      setMessages(historyMessages);
    } catch (err) {
      console.error('Error loading chat history:', err);
    } finally {
      setIsHistoryLoaded(true);
    }
  };

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('edsupp_token');
      if (!token) return [];
      const response = await axios.get(`${BACKEND_URL}/api/chat/conversations`, {
        params: { child_id: childId || undefined, is_child_view: isChildView },
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = response.data || [];
      setConversations(list);
      return list;
    } catch (err) {
      console.error('Error loading chat conversations:', err);
      return [];
    }
  };

  // On open, resume the most recently active thread (if any) instead of
  // starting on a blank screen every time the widget is reopened.
  const openMostRecentConversation = async () => {
    const list = await loadConversations();
    if (list.length > 0) {
      await loadChatHistory(list[0].conversation_id);
    } else {
      await loadChatHistory(null);
    }
  };

  const startNewChat = () => {
    if (isLoading) return;
    setShowHistoryPanel(false);
    setConversationId(null);
    setMessages([]);
    setError(null);
    setPendingAttachments([]);
    setInputMessage('');
  };

  const openConversation = async (id) => {
    if (isLoading || id === conversationId) {
      setShowHistoryPanel(false);
      return;
    }
    setShowHistoryPanel(false);
    await loadChatHistory(id);
  };

  // Clears only the currently open conversation (not every past chat) — the
  // history panel is left intact, refreshed so the now-empty thread drops
  // out of it.
  const clearChatHistory = async () => {
    try {
      const token = localStorage.getItem('edsupp_token');
      if (!token) {
        setError('Authentication required.');
        return;
      }
      if (conversationId) {
        await axios.delete(`${BACKEND_URL}/api/chat/history`, {
          params: { conversation_id: conversationId, child_id: childId || undefined, is_child_view: isChildView },
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      stopPlayback();
      ttsAudioCacheRef.current = {};      
      setMessages([]);
      await loadConversations();
    } catch (err) {
      console.error('Error clearing chat history:', err);
      setError('Could not clear this chat.');
    }
  };

  const sendMessageWithContent = async (content, options = {}) => {
    const { practiceRequest = null, displayContent = null } = options;
    const trimmed = (content || '').trim();
    const attachmentsToSend = pendingAttachments;
    if (!trimmed && attachmentsToSend.length === 0 && !practiceRequest) return;
    if (isLoading) return;

    setInputMessage('');
    setError(null);
    
    setPendingAttachments([]);

    // Add user message to UI with attachment metadata for inline rendering
    const newUserMessage = {
      role: 'user',
      content: displayContent != null ? displayContent : trimmed,
      timestamp: new Date().toISOString(),
      attachments: attachmentsToSend.map(a => ({
        attachment_id: a.attachment_id,
        kind: a.kind,
        content_type: a.content_type,
        name: a.name,
        url: a.url,
      })),
    };
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('edsupp_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setIsLoading(false);
        return;
      }
      
      // Debug: Log context being sent
      console.log('[AI Chat] Sending message with context:', {
        childId,
        taskId,
        evaluationId,
        noteId,
        attachmentCount: attachmentsToSend.length,
        hasContext: !!(evaluationId || taskId || noteId)
      });
      
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/message`,
        {
          message: practiceRequest ? '' : trimmed,
          provider: selectedProvider,
          model_id: selectedModel,
          child_id: childId,
          is_child_view: isChildView,
          batch_id: batchId,
          task_id: taskId,
          evaluation_id: evaluationId,
          note_id: noteId,
          attachment_ids: attachmentsToSend.map(a => a.attachment_id),
          practice_request: practiceRequest,
          hint_mode: isHintMode,
          conversation_id: conversationId,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Add assistant response to UI
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        nextSteps: response.data.next_steps || null,
        quizQuestions: response.data.quiz || null,
        quizTopic: practiceRequest?.topic || null,
      };
      
      setMessages(prev => [...prev, assistantMessage]);

      // The very first message of a chat mints its conversation_id server-side.
      if (!conversationId && response.data.conversation_id) {
        setConversationId(response.data.conversation_id);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to get response from AI. Please try again.';
      setError(errorMsg);
      
      // Add error message to chat
      setMessages(prev => [...prev, {
        role: 'error',
        content: errorMsg,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (isLoading) return;
    const trimmed = inputMessage.trim();
    if (!trimmed && pendingAttachments.length === 0) return;

    const content = isMathMode && trimmed ? `$${trimmed}$` : inputMessage;
    await sendMessageWithContent(content);
  };

  const handlePickFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const uploadAttachment = async (file) => {
    setIsUploadingAttachment(true);
    setError(null);
    try {
      const token = localStorage.getItem('edsupp_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/attachments`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      const attachment = response.data;
      setPendingAttachments(prev => [...prev, attachment]);
      // Generate a local preview URL for instant display
      try {
        const previewUrl = URL.createObjectURL(file);
        setAttachmentPreviews(prev => ({ ...prev, [attachment.attachment_id]: previewUrl }));
      } catch (_) {
        /* preview URL is best-effort; ignore failures */
      }
    } catch (err) {
      console.error('Attachment upload failed:', err);
      setError(err.response?.data?.detail || 'Image upload failed. Please try again.');
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type?.startsWith('image/') && file.type !== 'application/pdf') {
      setError('Only image or PDF files can be attached.');
      return;
    }
    await uploadAttachment(file);
  };

  const handleQuickAction = (prompt) => {
    if (isLoading || isUploadingAttachment || pendingAttachments.length === 0) return;
    // Merge with anything the user has already typed so they don't lose it.
    const typed = inputMessage.trim();
    const merged = typed ? `${prompt}\n\n${typed}` : prompt;
    sendMessageWithContent(merged);
  };

  const toggleHintMode = () => {
    if (isLoading || hasAccess !== true) return;
    setIsHintMode(prev => !prev);
  };

  const togglePracticeForm = () => {
    if (isLoading || hasAccess !== true) return;
    setShowPracticeForm(prev => !prev);
  };

  const submitPracticeRequest = () => {
    const topic = practiceTopic.trim();
    if (!topic || isLoading) return;
    setShowPracticeForm(false);
    setPracticeTopic('');
    sendMessageWithContent('', {
      practiceRequest: { topic, num_questions: practiceCount },
      displayContent: `📝 Give me ${practiceCount} practice questions on "${topic}"`,
    });
  };

  const handleNextStepClick = (suggestion) => {
    if (isLoading) return;
    sendMessageWithContent(suggestion);
  };

  // Persists a chat-generated quiz as an assignable content item, then opens
  // the same NotesAssignmentView modal Notes uses to pick recipients — the
  // actual /notes/{id}/assign call happens in its onAssign handler below.
  const handleAssignQuizClick = async (msg) => {
    if (isSavingQuiz) return;
    setIsSavingQuiz(true);
    setError(null);
    try {
      const token = localStorage.getItem('edsupp_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/quiz/save`,
        {
          topic: msg.quizTopic || 'Practice Quiz',
          questions: msg.quizQuestions,
          batch_id: batchId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuizToAssign(response.data.notes);
    } catch (err) {
      console.error('Error saving quiz for assignment:', err);
      setError(err.response?.data?.detail || 'Could not prepare this quiz for assignment.');
    } finally {
      setIsSavingQuiz(false);
    }
  };

  // Strip Markdown/LaTeX punctuation the TTS voice would otherwise read out
  // literally (asterisks, headings, code fences, dollar signs) before sending
  // an assistant reply off for narration.
  const stripMarkdownForSpeech = (text) => (text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/\$\$?/g, ' ')
    .replace(/[*_`#>]+/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

  // Mirrors the shared <audio> element's own events into React state, so the
  // play/pause/stop controls and progress bar stay in sync regardless of
  // which message triggered them (play/pause/ended fire the same way whether
  // playback was started here or resumed via the native element).
  useEffect(() => {
    const audio = ttsAudioRef.current;
    if (!audio) return undefined;
    const handleTimeUpdate = () => setTtsCurrentTime(audio.currentTime || 0);
    const handleLoadedMetadata = () => setTtsDuration(audio.duration || 0);
    const handleEnded = () => { setTtsIsPlaying(false); setTtsCurrentTime(0); };
    const handlePlay = () => setTtsIsPlaying(true);
    const handlePause = () => setTtsIsPlaying(false);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  const stopPlayback = () => {
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current.currentTime = 0;
    }
    setTtsActiveIdx(null);
    setTtsIsPlaying(false);
    setTtsCurrentTime(0);
    setTtsDuration(0);
  };

  const loadAudioAndPlay = (idx, dataUrl) => {
    const audio = ttsAudioRef.current;
    if (!audio) return;
    audio.src = dataUrl;
    audio.currentTime = 0;
    setTtsActiveIdx(idx);
    setTtsCurrentTime(0);
    setTtsDuration(0);
    audio.play().catch(() => setTtsIsPlaying(false));
  };

  const handlePlayClick = async (idx, content) => {
    // Already the loaded message: toggle play/pause in place instead of
    // stopping and re-fetching/replaying from the start.
    if (ttsActiveIdx === idx) {
      const audio = ttsAudioRef.current;
      if (!audio) return;
      if (audio.paused) {
        audio.play().catch(() => {});
      } else {
        audio.pause();
      }
      return;
    }

    // Switching to a different message: stop whatever was playing first.
    stopPlayback();

    const cached = ttsAudioCacheRef.current[idx];
    if (cached) {
      loadAudioAndPlay(idx, cached);
      return;
    }

    const spokenText = stripMarkdownForSpeech(content).slice(0, 4000);
    if (!spokenText) return;

    setTtsLoadingIdx(idx);
    try {
      const token = localStorage.getItem('edsupp_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/tts`,
        { text: spokenText, voice: 'nova' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const dataUrl = `data:${response.data.content_type || 'audio/mpeg'};base64,${response.data.audio_b64}`;
      ttsAudioCacheRef.current[idx] = dataUrl;
      loadAudioAndPlay(idx, dataUrl);
    } catch (err) {
      console.error('Chat TTS failed:', err);
      setError(err.response?.data?.detail || 'Could not read this reply aloud. Please try again.');
    } finally {
      setTtsLoadingIdx(null);
    }
  };

  // Stop any in-flight narration and clear its cache when the widget unmounts.
  useEffect(() => {
    return () => {
      ttsAudioRef.current?.pause();
      ttsAudioCacheRef.current = {};
    };
  }, []);

  const removePendingAttachment = (attachmentId) => {
    setPendingAttachments(prev => prev.filter(a => a.attachment_id !== attachmentId));
    setAttachmentPreviews(prev => {
      const next = { ...prev };
      const url = next[attachmentId];
      if (url) {
        try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
      }
      delete next[attachmentId];
      return next;
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getModelDisplayName = () => {
    const model = availableModels.find(
      m => m.provider === selectedProvider && m.model_id === selectedModel
    );
    return model ? model.display_name : 'AI Assistant';
  };

  const toggleHistoryPanel = () => {
    setShowHistoryPanel(prev => {
      const next = !prev;
      if (next) loadConversations();
      return next;
    });
  };

  const formatConversationTime = (timestamp) => {
    const date = normalizeTimestamp(timestamp);
    if (!date) return '';

    const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);

    if (diffDays <= 0) return formatMessageTime(timestamp);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) {
      return new Intl.DateTimeFormat('en-IN', { weekday: 'short' }).format(date);
    }

    return new Intl.DateTimeFormat('en-IN', {
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  if (!isOpen) {
    if (isSuppressed) {
      return null;
    }

    return (
      <button
        className={`ai-chat-fab${noBottomNav ? ' ai-chat-no-bottom-nav' : ''}`}
        onClick={() => setIsOpen(true)}
        title="Ask Eddy"
      >
        <span aria-hidden="true" className="text-xl leading-none">🧑‍🏫</span>
        {/*<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>*/}
      </button>
    );
  }

  // Don't render if still checking access
  const isCheckingAccess = hasAccess === null;

  return (
    <div
      className={`ai-chat-modal${noBottomNav ? ' ai-chat-no-bottom-nav' : ''}`}
      style={mathKeyboardOffset ? { '--math-kb-offset': `${mathKeyboardOffset}px` } : undefined}
    >
      <div className="ai-chat-container">
        {/* Header */}
        <div className="ai-chat-header">
          <div className="ai-chat-title">
            <span className="eddy-avatar">
              🧑‍🏫
              <span className="eddy-status-dot" aria-hidden="true" />
            </span>
            <div className="ai-chat-title-text">
              <span className="ai-chat-title-name">Eddy</span>
              <span className="ai-chat-title-sub hidden sm:inline">Your AI study buddy</span>
            </div>
            {/*{subscription?.plan?.name && subscription?.plan?.name !== 'Basic' && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-purple-600 text-white rounded-full">
                {subscription.plan.name.toUpperCase()}
              </span>
            )}*/}
          </div>
          <div className="ai-chat-header-actions">
            <div className="ai-chat-model-picker">
              <select
                id="model-select"
                value={`${selectedProvider}:${selectedModel}`}
                onChange={(e) => {
                  const [provider, model] = e.target.value.split(':');
                  setSelectedProvider(provider);
                  setSelectedModel(model);
                }}
                disabled={isLoading || hasAccess !== true}
                title="Choose AI model"
                aria-label="Choose AI model"
              >
                {availableModels.map(model => (
                  <option
                    key={`${model.provider}:${model.model_id}`}
                    value={`${model.provider}:${model.model_id}`}
                  >
                    {model.display_name}
                  </option>
                ))}
              </select>
              <span className="ai-chat-model-picker-chevron" aria-hidden="true">⌄</span>
            </div>
            <button
              className="ai-chat-new-chat-btn compact-action-btn"
              onClick={startNewChat}
              disabled={isLoading || hasAccess !== true}
              title="Start a new chat"
              aria-label="Start a new chat"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
              </svg>
            </button>
            <div className="ai-chat-history-wrapper" ref={historyPanelRef}>
              <button
                className={`ai-chat-history-btn compact-action-btn${showHistoryPanel ? ' active' : ''}`}
                onClick={toggleHistoryPanel}
                disabled={hasAccess !== true}
                title="View past chats"
                aria-label="View past chats"
                aria-expanded={showHistoryPanel}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3v5h5M12 7v5l4 2" />
                </svg>
              </button>
              {showHistoryPanel && (
                <div className="ai-chat-history-panel">
                  <div className="ai-chat-history-panel-title">Your chats</div>
                  {conversations.length === 0 ? (
                    <div className="ai-chat-history-empty">No past chats yet</div>
                  ) : (
                    <ul className="ai-chat-history-list">
                      {conversations.map((conv) => (
                        <li key={conv.conversation_id}>
                          <button
                            type="button"
                            className={`ai-chat-history-item${conv.conversation_id === conversationId ? ' active' : ''}`}
                            onClick={() => openConversation(conv.conversation_id)}
                          >
                            <span className="ai-chat-history-item-title">{conv.title}</span>
                            <span className="ai-chat-history-item-meta">
                              {formatConversationTime(conv.updated_at)} · {conv.message_count} msg{conv.message_count === 1 ? '' : 's'}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            {messages.length > 0 && (
              <button
                className="ai-chat-clear-btn compact-action-btn"
                onClick={() => setShowClearConfirm(true)}
                disabled={isLoading}
                title="Clear all chat history"
                aria-label="Clear all chat history"
              >
                🗑️
              </button>
            )}
            <button
              className="ai-chat-close compact-action-btn"
              onClick={() => setIsOpen(false)}
              title="Close"
              aria-label="Close chat"
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="ai-chat-messages" ref={chatContainerRef}>
          {hasAccess === null && (
            <div className="ai-chat-welcome">
              <p className="eddy-intro">Checking your AI chat access...</p>
              <p>Please wait a moment.</p>
            </div>
          )}

          {hasAccess === false && (
            <div className="ai-chat-welcome">
              <p className="eddy-intro">AI chat isn’t available for your account.</p>
              <p>Please check your subscription or log in again.</p>
            </div>
          )}

          {hasAccess === true && messages.length === 0 && (
            <div className="ai-chat-welcome">
              <div className="ai-chat-welcome-badge" aria-hidden="true">🧑‍🏫</div>
              <p className="eddy-intro">Hi! I'm Eddy, your AI study buddy 👋</p>
              {(evaluationId || taskId || noteId) ? (
                <p>I can see the {evaluationId ? 'evaluation' : taskId ? 'task' : 'note'} you're viewing. Ask me anything about it!</p>
              ) : (
                <p>Ask me anything about your studies, homework, or educational topics.</p>
              )}
              <div className="ai-chat-starter-prompts">
                {STARTER_PROMPTS.map((starter) => (
                  <button
                    key={starter.label}
                    type="button"
                    className="ai-chat-starter-prompt"
                    onClick={() => setInputMessage(`${starter.label}: `)}
                  >
                    <span aria-hidden="true">{starter.icon}</span>
                    {starter.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {messages.map((msg, idx) => (
            <div key={idx} className={`ai-chat-message ${msg.role}`}>
              {msg.role === 'user' && (
                <div className="message-avatar user-avatar">You</div>
              )}
              {msg.role === 'assistant' && (
                <div className="message-avatar ai-avatar">Ed</div>
              )}
              <div className={`message-column${(msg.quizQuestions && msg.quizQuestions.length > 0) ? ' message-column-wide' : ''}`}>
                <div className="message-content">
                  {(msg.attachments && msg.attachments.length > 0) && (
                    <div className="ai-chat-message-attachments">
                      {msg.attachments.map((att) => (
                        <AuthenticatedAttachmentPreview
                          key={att.attachment_id || att.url}
                          attachment={att}
                          fallbackUrl={attachmentPreviews[att.attachment_id]}
                        />
                      ))}
                    </div>
                  )}
                  {msg.content && (
                    msg.role === 'error' ? (
                      <div>{msg.content}</div>
                    ) : (
                      <ReactMarkdown
                        remarkPlugins={[remarkMath]}
                        rehypePlugins={[rehypeKatex, rehypeRaw]}
                        components={{
                          // Ensure proper styling for lists
                          ul: ({node, ...props}) => <ul style={{marginLeft: '20px', listStyleType: 'disc'}} {...props} />,
                          ol: ({node, ...props}) => <ol style={{marginLeft: '20px', listStyleType: 'decimal'}} {...props} />,
                          li: ({node, ...props}) => <li style={{marginBottom: '4px'}} {...props} />,
                          // Bold and italic
                          strong: ({node, ...props}) => <strong style={{fontWeight: 'bold'}} {...props} />,
                          em: ({node, ...props}) => <em style={{fontStyle: 'italic'}} {...props} />,
                          // Paragraphs with spacing
                          p: ({node, ...props}) => <p style={{marginBottom: '8px'}} {...props} />
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    )
                  )}
                </div>
                {msg.role === 'assistant' && msg.quizQuestions && msg.quizQuestions.length > 0 && (
                  <>
                    <QuizFlashcards questions={msg.quizQuestions} className="ai-chat-inline-quiz" />
                    {!isChildView && ['parent', 'coaching_teacher', 'school_teacher'].includes(user?.role) && (
                      <button
                        type="button"
                        className="ai-chat-assign-quiz-btn"
                        onClick={() => handleAssignQuizClick(msg)}
                        disabled={isSavingQuiz}
                      >
                        {isSavingQuiz ? 'Preparing…' : '📤 Assign this quiz'}
                      </button>
                    )}
                  </>
                )}
                {msg.role !== 'error' && (
                  <div className="message-footer">
                    {msg.timestamp && (
                      <span className="message-timestamp">{formatMessageTime(msg.timestamp)}</span>
                    )}
                    {msg.role === 'assistant' && msg.content && ttsActiveIdx !== idx && (
                      <button
                        type="button"
                        className="ai-chat-listen-btn"
                        onClick={() => handlePlayClick(idx, msg.content)}
                        disabled={ttsLoadingIdx === idx}
                        title="Read this reply aloud"
                        aria-label="Read this reply aloud"
                      >
                        {ttsLoadingIdx === idx ? (
                          <span className="ai-chat-attach-spinner" aria-hidden="true" />
                        ) : (
                          <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
                            <path d="M4 9v6h4l5 5V4L8 9H4z" />
                            <path d="M16.5 12a3.5 3.5 0 00-1.79-3.06.75.75 0 10-.72 1.31A2 2 0 0115 12a2 2 0 01-1 1.75.75.75 0 10.72 1.31A3.5 3.5 0 0016.5 12z" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                )}
                {msg.role === 'assistant' && msg.content && ttsActiveIdx === idx && (
                  <div className="ai-chat-tts-controls">
                    <button
                      type="button"
                      className="ai-chat-tts-btn"
                      onClick={() => handlePlayClick(idx, msg.content)}
                      title={ttsIsPlaying ? 'Pause' : 'Play'}
                      aria-label={ttsIsPlaying ? 'Pause' : 'Play'}
                    >
                      {ttsIsPlaying ? (
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
                          <rect x="6" y="5" width="4" height="14" rx="1" />
                          <rect x="14" y="5" width="4" height="14" rx="1" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      className="ai-chat-tts-btn"
                      onClick={stopPlayback}
                      title="Stop"
                      aria-label="Stop"
                    >
                      <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor" aria-hidden="true">
                        <rect x="6" y="6" width="12" height="12" rx="1.5" />
                      </svg>
                    </button>
                    <div
                      className="ai-chat-tts-progress"
                      role="progressbar"
                      aria-valuenow={Math.round(ttsCurrentTime)}
                      aria-valuemin={0}
                      aria-valuemax={Math.round(ttsDuration) || 0}
                    >
                      <div
                        className="ai-chat-tts-progress-fill"
                        style={{ width: `${ttsDuration ? Math.min((ttsCurrentTime / ttsDuration) * 100, 100) : 0}%` }}
                      />
                    </div>
                    <span className="ai-chat-tts-time">
                      {formatRecordingTime(Math.floor(ttsCurrentTime))} / {formatRecordingTime(Math.floor(ttsDuration || 0))}
                    </span>
                  </div>
                )}
                {msg.role === 'assistant' && idx === messages.length - 1 && msg.nextSteps && msg.nextSteps.length > 0 && (
                  <div className="ai-chat-next-steps" role="toolbar" aria-label="Suggested next steps">
                    {msg.nextSteps.map((suggestion, stepIdx) => (
                      <button
                        key={stepIdx}
                        type="button"
                        className="ai-chat-next-step-chip"
                        onClick={() => handleNextStepClick(suggestion)}
                        disabled={isLoading}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="ai-chat-message assistant">
              <div className="message-avatar ai-avatar">Ed</div>
              <div className="message-column">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
          <audio ref={ttsAudioRef} className="hidden" />
        </div>

        {/* Input */}
        <div className="ai-chat-input-wrapper">
          {pendingAttachments.length > 0 && (
            <div className="ai-chat-pending-attachments">
              {pendingAttachments.map((att) => {
                const previewSrc = attachmentPreviews[att.attachment_id];
                const isPdf = att.kind === 'pdf' || att.content_type === 'application/pdf';
                return (
                  <div className="ai-chat-pending-chip" key={att.attachment_id}>
                    {previewSrc && !isPdf ? (
                      <img
                        src={previewSrc}
                        alt={att.name || 'attachment'}
                        className="ai-chat-pending-thumb"
                      />
                    ) : (
                      <span className="ai-chat-pending-icon" aria-hidden="true">{isPdf ? '📄' : '🖼️'}</span>
                    )}
                    <span className="ai-chat-pending-name">{att.name || (isPdf ? 'document.pdf' : 'image')}</span>
                    <button
                      type="button"
                      className="ai-chat-pending-remove"
                      onClick={() => removePendingAttachment(att.attachment_id)}
                      title="Remove attachment"
                      aria-label="Remove attachment"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {pendingAttachments.length > 0 && (
            <div className="ai-chat-quick-actions" role="toolbar" aria-label="Quick actions">
              {IMAGE_QUICK_ACTIONS.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  className="ai-chat-quick-action"
                  onClick={() => handleQuickAction(action.prompt)}
                  disabled={isLoading || isUploadingAttachment}
                  title={action.prompt}
                >
                  <span className="ai-chat-quick-action-icon" aria-hidden="true">{action.icon}</span>
                  <span className="ai-chat-quick-action-label">{action.label}</span>
                </button>
              ))}
            </div>
          )}
          {showPracticeForm && (
            <div className="ai-chat-practice-form">
              <button
                type="button"
                className="ai-chat-attach-btn compact-action-btn"
                onClick={handlePickFile}
                disabled={isLoading || isUploadingAttachment}
                title="Attach an image or PDF to base the questions on"
                aria-label="Attach an image or PDF to base the questions on"
              >
                {isUploadingAttachment ? (
                  <span className="ai-chat-attach-spinner" aria-hidden="true" />
                ) : (
                  <svg
                    className="ai-chat-attach-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 5v14M5 12h14" />
                  </svg>
                )}
              </button>
              <input
                type="text"
                className="ai-chat-practice-topic-input"
                placeholder="Topic (e.g. photosynthesis)"
                value={practiceTopic}
                onChange={(e) => setPracticeTopic(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitPracticeRequest(); } }}
                autoFocus
              />
              <div className="ai-chat-practice-count-group" role="group" aria-label="Number of questions">
                {[3, 5, 8].map((count) => (
                  <button
                    key={count}
                    type="button"
                    className={`ai-chat-practice-count-btn compact-action-btn${practiceCount === count ? ' active' : ''}`}
                    onClick={() => setPracticeCount(count)}
                  >
                    {count}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="ai-chat-practice-submit-btn compact-action-btn"
                onClick={submitPracticeRequest}
                disabled={!practiceTopic.trim() || isLoading}
                title={
                  pendingAttachments.length > 0
                    ? 'Ask Eddy for these practice questions, based on the attached image(s)'
                    : 'Ask Eddy for these practice questions'
                }
                aria-label="Ask Eddy for practice questions"
              >
                <span className="ai-chat-practice-submit-icon" aria-hidden="true">✓</span>
                <span className="ai-chat-practice-submit-label">Submit</span>
              </button>
              <button
                type="button"
                className="ai-chat-practice-cancel-btn compact-action-btn"
                onClick={() => setShowPracticeForm(false)}
                aria-label="Cancel"
              >
                ×
              </button>
            </div>
          )}
          <div className={`ai-chat-input-container${isRecording ? ' is-recording' : ''}`}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="ai-chat-file-input"
              onChange={handleFileChange}
            />
            {isRecording ? (
              <div className="ai-chat-recording-row">
                <span className="ai-chat-recording-dot" aria-hidden="true" />
                <div className="ai-chat-recording-bars" aria-hidden="true">
                  <span /><span /><span /><span /><span />
                </div>
                <span className="ai-chat-recording-time">{formatRecordingTime(recordingSeconds)}</span>
                <button
                  type="button"
                  className="ai-chat-recording-cancel"
                  onClick={cancelRecording}
                  title="Cancel recording"
                  aria-label="Cancel recording"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ai-chat-send-btn ai-chat-stop-btn compact-action-btn"
                  onClick={stopRecording}
                  title="Stop and use recording"
                  aria-label="Stop and use recording"
                >
                  <span className="ai-chat-stop-icon" aria-hidden="true" />
                </button>
              </div>
            ) : (
              <>
                {/* Row 1: the text field on its own full-width line */}
                {isMathMode ? (
                  isMathLiveLoaded ? (
                    // eslint-disable-next-line react/no-unknown-property
                    <math-field
                      ref={mathFieldRef}
                      class="ai-chat-input ai-chat-math-field"
                      math-virtual-keyboard-policy="auto"
                    />
                  ) : (
                    <div className="ai-chat-input ai-chat-math-field ai-chat-math-loading">
                      Loading math keyboard…
                    </div>
                  )
                ) : (
                  <textarea
                    className="ai-chat-input"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={
                      hasAccess === true
                        ? (pendingAttachments.length > 0
                            ? (isCompactViewport
                                ? 'Ask about this file…'
                                : 'Ask Eddy about this file (e.g., solve this, check my answer)…')
                            : (isCompactViewport ? 'Type your message…' : 'Type your message or attach an image/PDF…'))
                        : 'AI chat is unavailable.'
                    }
                    disabled={isLoading || hasAccess !== true}
                    rows={1}
                  />
                )}

                {/* Row 2: attach/math/practice toggles on the left, send/mic on the right */}
                <div className="ai-chat-input-toolbar">
                  <div className="ai-chat-input-toolbar-left">
                    <button
                      type="button"
                      className="ai-chat-attach-btn compact-action-btn"
                      onClick={handlePickFile}
                      disabled={isLoading || hasAccess !== true || isUploadingAttachment}
                      title="Attach an image or PDF to ask Eddy about it"
                      aria-label="Attach image or PDF"
                    >
                      {isUploadingAttachment ? (
                        <span className="ai-chat-attach-spinner" aria-hidden="true" />
                      ) : (
                        <svg
                          className="ai-chat-attach-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 5v14M5 12h14" />
                        </svg>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`ai-chat-math-toggle-btn compact-action-btn${isMathMode ? ' active' : ''}`}
                      onClick={toggleMathMode}
                      disabled={isLoading || hasAccess !== true}
                      title={isMathMode ? 'Switch to plain text' : 'Enter a math equation'}
                      aria-label="Toggle math keyboard"
                      aria-pressed={isMathMode}
                    >
                      Σ
                    </button>
                    <button
                      type="button"
                      className={`ai-chat-hint-toggle-btn compact-action-btn${isHintMode ? ' active' : ''}`}
                      onClick={toggleHintMode}
                      disabled={isLoading || hasAccess !== true}
                      title={isHintMode ? 'Hint mode is on — Eddy guides with hints instead of full answers' : 'Turn on hint mode'}
                      aria-label="Toggle hint-first mode"
                      aria-pressed={isHintMode}
                    >
                      <span aria-hidden="true">🧭</span>
                    </button>
                    <button
                      type="button"
                      className={`ai-chat-practice-toggle-btn compact-action-btn${showPracticeForm ? ' active' : ''}`}
                      onClick={togglePracticeForm}
                      disabled={isLoading || hasAccess !== true}
                      title="Practice questions on a topic"
                      aria-label="Ask for practice questions"
                      aria-pressed={showPracticeForm}
                    >
                      <span aria-hidden="true">📝</span>
                    </button>
                  </div>
                  {isTranscribing ? (
                    <button
                      type="button"
                      className="ai-chat-send-btn compact-action-btn"
                      disabled
                      title="Transcribing…"
                      aria-label="Transcribing"
                    >
                      <span className="ai-chat-attach-spinner ai-chat-attach-spinner-light" aria-hidden="true" />
                    </button>
                  ) : (!inputMessage.trim() && pendingAttachments.length === 0 && !isMathMode) ? (
                    <button
                      type="button"
                      className="ai-chat-send-btn ai-chat-mic-btn compact-action-btn"
                      onClick={startRecording}
                      disabled={isLoading || hasAccess !== true}
                      title="Record a voice message"
                      aria-label="Record a voice message"
                    >
                      <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">
                        <path d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3z" />
                        <path d="M19 11a1 1 0 10-2 0 5 5 0 01-10 0 1 1 0 10-2 0 7 7 0 006 6.92V20H9a1 1 0 100 2h6a1 1 0 100-2h-2v-2.08A7 7 0 0019 11z" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      className="ai-chat-send-btn compact-action-btn"
                      onClick={sendMessage}
                      disabled={
                        isLoading
                        || hasAccess !== true
                        || isUploadingAttachment
                        || (!inputMessage.trim() && pendingAttachments.length === 0)
                      }
                      title="Send message"
                      aria-label="Send message"
                    >
                      <svg className="ai-chat-send-icon" viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">
                        <path d="M12 4l-1.4 1.4L16.2 11H4v2h12.2l-5.6 5.6L12 20l8-8-8-8z" />
                      </svg>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {showClearConfirm && (
        <ConfirmModal
          title="Clear this chat"
          message="Are you sure you want to clear this chat? This action cannot be undone. Your other past chats won't be affected."
          confirmLabel="Clear chat"
          onCancel={() => setShowClearConfirm(false)}
          onConfirm={async () => {
            setShowClearConfirm(false);
            await clearChatHistory();
          }}
        />
      )}
      {quizToAssign && (
        <div className="ai-chat-assign-quiz-overlay">
          <div className="ai-chat-assign-quiz-dialog">
            <NotesAssignmentView
              notes={quizToAssign}
              onGenerateNew={() => setQuizToAssign(null)}
              onSaveClose={() => setQuizToAssign(null)}
              onAssign={async (assignmentData) => {
                const token = localStorage.getItem('edsupp_token');
                await axios.post(
                  `${BACKEND_URL}/api/notes/${quizToAssign.id}/assign`,
                  assignmentData,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                setQuizToAssign(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AIChatAssistant;
